import { useCallback, useRef, useState } from 'react';

import { useLatest } from './useLatest';

/**
 * Ensures an async function only runs one invocation at a time.
 * Subsequent calls while running are dropped (not queued).
 *
 * Uses a ref for synchronous guard (prevents race conditions from rapid
 * double-calls) and state for UI (disable buttons, show spinners).
 *
 * ## Why both ref and state?
 *
 * - **Ref**: Synchronous read/write prevents races. Two calls in the same
 *   event loop won't both pass the guard.
 * - **State**: Triggers re-render so UI can react to `isRunning`.
 *
 * ## Example
 *
 * ```tsx
 * function RecordButton() {
 *   const { run: startRecording, isRunning } = useSingleFlight(
 *     useCallback(async () => {
 *       const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
 *       // ... start recording
 *     }, [])
 *   );
 *
 *   return (
 *     <button onClick={startRecording} disabled={isRunning}>
 *       {isRunning ? 'Starting...' : 'Record'}
 *     </button>
 *   );
 * }
 * ```
 *
 * ## When to use
 *
 * - Creating sessions or resources that shouldn't be duplicated
 * - Starting recordings or other stateful operations
 * - Any async operation triggered by user interaction that could be clicked rapidly
 *
 * @param fn - The async function to wrap
 * @returns `run` - wrapped function, `isRunning` - whether currently executing
 */
export function useSingleFlight<
  T extends (...args: never[]) => Promise<unknown>,
>(
  fn: T,
): {
  run: (...args: Parameters<T>) => Promise<Awaited<ReturnType<T>> | undefined>;
  isRunning: boolean;
} {
  const fnRef = useLatest(fn); // Avoid stale closure if fn changes
  const runningRef = useRef(false);
  const [isRunning, setIsRunning] = useState(false);

  const run = useCallback(
    async (
      ...args: Parameters<T>
    ): Promise<Awaited<ReturnType<T>> | undefined> => {
      if (runningRef.current) return undefined;

      // Check and set happen synchronously in the same task before any await. No interleaving possible.
      // eslint-disable-next-line require-atomic-updates
      runningRef.current = true;
      setIsRunning(true);

      try {
        return (await fnRef.current(...args)) as Awaited<ReturnType<T>>;
      } finally {
        // Setting false is idempotent and always the correct final state regardless of any race.
        // eslint-disable-next-line require-atomic-updates
        runningRef.current = false;
        setIsRunning(false);
      }
    },
    // from useLatest; including it would defeat the purpose of having a stable `run`.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fnRef is a stable ref
    [],
  );

  return { run, isRunning };
}
